import { API_BASE_URL } from "./auth-client";

// Types based on OpenAPI spec
export interface Lead {
  id: number;
  name: string | null;
  phone: string;
  status: "new" | "initiated" | "converted" | "temporary" | "invalid";
  source: string | null;
  createdAt: number;
  updatedAt: number;
  aiEnabled: boolean;
  aiDisabledBy: string | null;
  aiDisabledAt: number | null;
}

export interface Alert {
  id: number;
  leadId: number;
  reason: string;
  agentNote: string;
  incomingText: string;
  createdAt: number;
  acknowledgedAt: number | null;
  acknowledgedBy: string | null;
}

export interface AlertWithLead {
  alert: Alert;
  lead: Lead | null;
}

export interface Message {
  id: number;
  leadId: number;
  role: "user" | "assistant";
  content: string;
  deliveryStatus?: "sending" | "sent" | "delivered" | "read" | "failed";
  deliveredAt?: number;
  readAt?: number;
  createdAt: number;
}

export interface Note {
  id: number;
  leadId: number;
  content: string;
  createdBy: string;
  updatedBy: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface ChatsResponse {
  page: number;
  limit: number;
  chats: Lead[];
}

export interface MessagesResponse {
  page: number;
  limit: number;
  lead: Lead;
  messages: Message[];
}

export interface NotesResponse {
  page: number;
  limit: number;
  notes: Note[];
}

export interface MeResponse {
  email: string | null;
  isAdmin: boolean;
}

export interface TeamUser {
  id: string;
  name: string;
  email: string;
  image: string | null;
  createdAt: number;
  updatedAt: number;
  role: "admin" | "member";
}

export interface TeamUsersResponse {
  users: TeamUser[];
}

export interface GlobalAIResponse {
  enabled: boolean;
  updatedAt: number | null;
  updatedBy: string | null;
}

export interface AlertsResponse {
  page: number;
  limit: number;
  total: number;
  alerts: AlertWithLead[];
}

export interface NoteWithLead {
  note: Note;
  lead: Lead;
}

export interface NotesAllResponse {
  page: number;
  limit: number;
  notes: NoteWithLead[];
}

export interface PromptResponse {
  prompt: string;
}

export interface KBFact {
  id: number;
  key: string;
  valueJson: string;
  tags: string;
  isActive: boolean;
  updatedBy: string | null;
  updatedAt: number;
  status: "filled" | "placeholder" | "missing";
}

export interface KBEntrySearchResult {
  id: number;
  title: string;
  tags: string;
  snippet: string;
  isActive: boolean;
  updatedAt: number;
}

export interface KBEntry {
  id: number;
  title: string;
  tags: string;
  contentMd: string;
  isActive: boolean;
  updatedBy: string | null;
  updatedAt: number;
}

// API client with typed methods
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

	    if (!response.ok) {
	      const parsed = await response.json().catch(() => null);
	      const msg =
	        parsed &&
	        typeof parsed === "object" &&
	        "error" in parsed &&
	        typeof (parsed as any).error === "string"
	          ? (parsed as any).error
	          : `HTTP ${response.status}`;
	      throw new Error(msg);
	    }

    return response.json();
  }

  // Chats
  async getChats(params?: { page?: number; limit?: number; search?: string }): Promise<ChatsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.limit) searchParams.set("limit", String(params.limit));
    if (params?.search) searchParams.set("search", params.search);
    
    const query = searchParams.toString();
    return this.request(`/api/chats${query ? `?${query}` : ""}`);
  }

  // Messages
  async getMessages(leadId: number, params?: { page?: number; limit?: number }): Promise<MessagesResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.limit) searchParams.set("limit", String(params.limit));
    
    const query = searchParams.toString();
    return this.request(`/api/chats/${leadId}/messages${query ? `?${query}` : ""}`);
  }

  async sendMessage(leadId: number, text: string): Promise<{ success: boolean }> {
    return this.request(`/api/chats/${leadId}/messages`, {
      method: "POST",
      body: JSON.stringify({ text }),
    });
  }

  // AI Mode
  async toggleAIMode(leadId: number, enabled: boolean, actor?: string): Promise<{ success: boolean; lead: Lead }> {
    return this.request(`/api/chats/${leadId}/ai-mode`, {
      method: "POST",
      body: JSON.stringify({ enabled, actor }),
    });
  }

  async renameChat(leadId: number, name: string): Promise<{ success: boolean; lead: Lead }> {
    return this.request(`/api/chats/${leadId}`, {
      method: "PUT",
      body: JSON.stringify({ name }),
    });
  }

  // Notes
  async getNotes(leadId: number, params?: { page?: number; limit?: number }): Promise<NotesResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.limit) searchParams.set("limit", String(params.limit));

    const query = searchParams.toString();
    return this.request(`/api/chats/${leadId}/notes${query ? `?${query}` : ""}`);
  }

  async addNote(leadId: number, content: string): Promise<{ success: boolean; note: Note }> {
    return this.request(`/api/chats/${leadId}/notes`, {
      method: "POST",
      body: JSON.stringify({ content }),
    });
  }

  async updateNote(leadId: number, noteId: number, content: string): Promise<{ success: boolean; note: Note }> {
    return this.request(`/api/chats/${leadId}/notes/${noteId}`, {
      method: "PUT",
      body: JSON.stringify({ content }),
    });
  }

  async deleteNote(leadId: number, noteId: number): Promise<{ success: boolean }> {
    return this.request(`/api/chats/${leadId}/notes/${noteId}`, {
      method: "DELETE",
    });
  }

  // Me / System
  async getMe(): Promise<MeResponse> {
    return this.request("/api/me");
  }

  async getTeamUsers(): Promise<TeamUsersResponse> {
    return this.request("/api/team/users");
  }

  async getGlobalAI(): Promise<GlobalAIResponse> {
    return this.request("/api/system/ai");
  }

  async setGlobalAI(enabled: boolean): Promise<GlobalAIResponse> {
    return this.request("/api/system/ai", {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    });
  }

  // Alerts
  async getAlerts(params?: { page?: number; limit?: number; status?: "open" }): Promise<AlertsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.limit) searchParams.set("limit", String(params.limit));
    if (params?.status) searchParams.set("status", params.status);

    const query = searchParams.toString();
    return this.request(`/api/alerts${query ? `?${query}` : ""}`);
  }

  async ackAlert(alertId: number): Promise<{ success: boolean; alert: Alert }> {
    return this.request(`/api/alerts/${alertId}/ack`, { method: "POST" });
  }

  // Notes (all)
  async listNotesAll(params?: { page?: number; limit?: number; q?: string; leadId?: number }): Promise<NotesAllResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.limit) searchParams.set("limit", String(params.limit));
    if (params?.q) searchParams.set("q", params.q);
    if (params?.leadId) searchParams.set("leadId", String(params.leadId));

    const query = searchParams.toString();
    return this.request(`/api/notes${query ? `?${query}` : ""}`);
  }

  // Prompt
  async getPrompt(): Promise<PromptResponse> {
    return this.request("/api/prompt");
  }

  async updatePrompt(prompt: string): Promise<PromptResponse> {
    return this.request("/api/prompt", {
      method: "PUT",
      body: JSON.stringify({ prompt }),
    });
  }

  // Knowledge Base
  async getKBFacts(): Promise<{ facts: KBFact[] }> {
    return this.request("/api/kb/facts");
  }

  async updateKBFact(key: string, input: { valueJson: string; tags: string; isActive: boolean }): Promise<{ fact: KBFact }> {
    return this.request(`/api/kb/facts/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  }

  async searchKBEntries(params: { query: string; limit?: number }): Promise<{ results: KBEntrySearchResult[] }> {
    const searchParams = new URLSearchParams();
    searchParams.set("query", params.query);
    if (params.limit) searchParams.set("limit", String(params.limit));
    return this.request(`/api/kb/entries?${searchParams.toString()}`);
  }

  async createKBEntry(input: { title: string; tags: string; contentMd: string; isActive: boolean }): Promise<{ entry: { id: number } }> {
    return this.request("/api/kb/entries", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  async getKBEntry(id: number): Promise<{ entry: KBEntry }> {
    return this.request(`/api/kb/entries/${id}`);
  }

  async updateKBEntry(id: number, input: { title?: string; tags?: string; contentMd?: string; isActive?: boolean }): Promise<{ entry: { id: number } }> {
    return this.request(`/api/kb/entries/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  }

  async deleteKBEntry(id: number): Promise<{ success: boolean }> {
    return this.request(`/api/kb/entries/${id}`, {
      method: "DELETE",
    });
  }
}

export const api = new ApiClient(API_BASE_URL);
